/**
 * The external dependencies
 */
import React, { ReactElement, useEffect } from 'react';

import {
  CreateWorkMaterialDto,
  GetChangeDto,
  ListWorkMaterialDto,
  CreatorRole,
  ProcessListForChangeDto,
  UpdateChangeDtoStatusEnum,
} from '@codefluegel/zeta-change-typescript-client';
import {
  Autocomplete,
  Box,
  Button,
  css,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useWorkMaterialApiFactory } from '../../ApiClient';
import { getDepartmentById } from '../../utils/utils';
import { NotificationType, NotificationTypes } from '../../types/notification';
import NotificationMessages from '../../constants/notification';
import {
  checkServiceMaterialAccess,
  isChangeManager,
  isKTM,
} from '../../utils/permissionHelper';

const measureUnitOptions = ['Key', 'Standard', 'Large'];

const allAreas = [
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '10',
  '11',
  '12',
  '13',
  '14',
];

type MaterialFormData = {
  wg: { wg: string; description: string } | null;
  amount: number;
  price: number | null;
  measureUnit: string | null;
  areaId: string | undefined;
  description: string;
};

export type MaterialModalProps = {
  isOpen: boolean;
  handleModal: () => void;
  creatorRole: CreatorRole;
  change: GetChangeDto;
  currentProcess: ProcessListForChangeDto;
  pushNewMaterialPackage: (newMaterialPackage: ListWorkMaterialDto) => void;
  replaceUpdatedMaterialPackage: (materialPackage: ListWorkMaterialDto) => void;
  removeMaterialPackage: (materialPackageId: number) => void;
  selectedMaterial?: ListWorkMaterialDto;
  setNotification: (data: NotificationType) => void;
};

const MaterialModal = ({
  isOpen,
  handleModal,
  change,
  currentProcess,
  creatorRole,
  pushNewMaterialPackage,
  replaceUpdatedMaterialPackage,
  setNotification,
  removeMaterialPackage,
  selectedMaterial,
}: MaterialModalProps): ReactElement<any, any> => {
  const { t } = useTranslation();
  const workMaterialApi = useWorkMaterialApiFactory();
  const NotificationMessage = NotificationMessages();

  const isKTMorCM =
    isKTM(change, Number(currentProcess?.pId)) || isChangeManager(change);

  const roleAccess = checkServiceMaterialAccess(
    change,
    selectedMaterial?.creatorRole,
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm<MaterialFormData>({
    mode: 'onChange',
    defaultValues: selectedMaterial
      ? {
          wg: selectedMaterial.wg
            ? {
                wg: selectedMaterial.wg,
                description: selectedMaterial.title || '',
              }
            : null,
          amount: selectedMaterial.amount || 0,
          price: selectedMaterial.price || null,
          measureUnit: selectedMaterial.measureUnit || null,
          areaId: selectedMaterial.areaId
            ? String(selectedMaterial.areaId)
            : undefined,
          description: selectedMaterial.description || '',
        }
      : {
          wg: null,
          amount: 0,
          price: null,
          measureUnit: null,
          areaId: undefined,
          description: '',
        },
  });

  const materialData = [
    { wg: 'WG00', description: t('material.data.helpers') },
    { wg: 'WG01', description: t('material.data.equipment') },
    { wg: 'WG02', description: t('material.data.armaturen') },
    { wg: 'WG03', description: t('material.data.loggers') },
    {
      wg: 'WG04',
      description: t('material.data.others'),
    },
    { wg: 'WG05', description: t('material.data.pipes') },
  ];

  useEffect(() => {
    if (selectedMaterial) {
      reset({
        wg: selectedMaterial.wg
          ? {
              wg: selectedMaterial.wg,
              description: selectedMaterial.title || '',
            }
          : null,
        amount: selectedMaterial.amount || 0,
        price: selectedMaterial.price || null,
        measureUnit: selectedMaterial.measureUnit || null,
        description: selectedMaterial.description || '',
        areaId: selectedMaterial.areaId
          ? String(selectedMaterial.areaId)
          : undefined,
      });
    } else {
      reset({
        wg: null,
        amount: 0,
        price: null,
        measureUnit: null,
        areaId: undefined,
        description: '',
      });
    }
  }, [selectedMaterial, reset, isOpen]);

  const deleteWorkMaterial = async () => {
    try {
      if (selectedMaterial) {
        await workMaterialApi.workMaterialsControllerRemove({
          id: selectedMaterial.id,
        });

        removeMaterialPackage(selectedMaterial.id);
        setNotification({
          type: NotificationTypes.success,
          message: NotificationMessage.workPackageCreate,
        });
      }

      handleModal();
    } catch (e: any) {
      setNotification({
        type: NotificationTypes.error,
        message: e.response.data.message || NotificationMessage.error,
      });
    }
  };

  const onSubmit: SubmitHandler<MaterialFormData> = async data => {
    if (!data.wg) return;
    const groupNo = getDepartmentById(
      Number(currentProcess?.pId ?? 0),
      change?.commonProject?.navProcessToCommonProjectRelations,
    )?.groupNo;
    const payload: CreateWorkMaterialDto = {
      position: 0,
      wg: data.wg.wg,
      amount: Number(data.amount),
      price: data.price ? Number(data.price) : null,
      measureUnit: data.measureUnit || '',
      areaId: Number(data.areaId || '0'),
      processId: Number(currentProcess.id),
      title: data.wg.description,
      description: data.description,
      groupNo: groupNo || 0,
      creatorRole,
    };
    try {
      if (selectedMaterial) {
        // Update existing
        const response = await workMaterialApi.workMaterialsControllerUpdate({
          id: selectedMaterial.id,
          updateWorkMaterialDto: payload,
        });
        if (response?.data) {
          replaceUpdatedMaterialPackage(response.data);
        }
      } else {
        // Create new
        const response = await workMaterialApi.workMaterialsControllerCreate({
          createWorkMaterialDto: payload,
        });
        if (response?.data) {
          pushNewMaterialPackage(response.data);
        }
      }
      handleModal();
    } catch (error) {
      console.error('Failed to save work material', error);
    }
  };

  return (
    <Dialog open={isOpen} onClose={handleModal} fullWidth maxWidth="sm">
      <DialogTitle>{t('material.new')}</DialogTitle>
      <DialogContent>
        <form id="material-form" onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name="wg"
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, value } }) => (
              <Autocomplete
                options={materialData}
                value={value}
                getOptionLabel={option =>
                  `${option.wg} - ${option.description}`
                }
                onChange={(event, item) => {
                  onChange(item);
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    variant="standard"
                    label={t('material.material')}
                    placeholder={t('material.select')}
                    required
                    sx={{ mt: 2 }}
                  />
                )}
              />
            )}
          />
          <Controller
            name="amount"
            control={control}
            rules={{ required: true, min: 1 }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                variant="standard"
                label={t('material.amount')}
                type="number"
                required
                sx={{ mt: 2 }}
                value={field.value ?? ''}
                onChange={e =>
                  field.onChange(
                    e.target.value === '' ? '' : Number(e.target.value),
                  )
                }
              />
            )}
          />
          <Controller
            name="price"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                variant="standard"
                label={t('material.price')}
                type="number"
                sx={{ mt: 2 }}
              />
            )}
          />
          <Controller
            name="measureUnit"
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, value } }) => (
              <Autocomplete
                options={measureUnitOptions}
                value={value}
                onChange={(event, item) => {
                  onChange(item);
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    fullWidth
                    variant="standard"
                    label={t('material.unit')}
                    required
                    sx={{ mt: 2 }}
                  />
                )}
              />
            )}
          />
          <Controller
            name="areaId"
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, value } }) => (
              <Autocomplete
                options={allAreas.map(num => String(num).padStart(2, '0'))}
                value={value ? String(value).padStart(2, '0') : ''}
                onChange={(_, newValue) => onChange(newValue || undefined)}
                renderInput={params => (
                  <TextField
                    {...params}
                    fullWidth
                    variant="standard"
                    label={t('Area')}
                    required
                    sx={{ mt: 2 }}
                  />
                )}
              />
            )}
          />
          <Controller
            name="description"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                variant="standard"
                label={t('material.description')}
                multiline
                rows={4}
                sx={{ mt: 2 }}
                required
              />
            )}
          />
        </form>
      </DialogContent>
      <DialogActions>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          {isKTMorCM && selectedMaterial && (
            <Button
              css={css`
                background: #9b9b9b;
                color: #00223b;

                &:hover {
                  background: #9b9b9b;
                }
              `}
              disabled={
                !isValid ||
                !(
                  change?.status === UpdateChangeDtoStatusEnum.InEvaluation ||
                  change?.status === UpdateChangeDtoStatusEnum.EvalRework
                )
              }
              variant="contained"
              component="span"
              onClick={deleteWorkMaterial}
            >
              {t('buttons.delete')}
            </Button>
          )}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={handleModal}>{t('buttons.close')}</Button>
            <Button
              type="submit"
              form="material-form"
              variant="contained"
              color="primary"
              disabled={!isValid || !roleAccess}
            >
              {t('buttons.save')}
            </Button>
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

MaterialModal.defaultProps = {
  selectedMaterial: undefined,
};

export default MaterialModal;
